import { useTranslation } from 'react-i18next';
import { KoobAlert } from '@koob/ui';
import { StayFlexibleCodeEnum } from '@koob/enums';

/**
 * KD-3496 — the room-nights an early check-in / late check-out needs outside the
 * booked window.
 *
 * The supplement is already in the booking price: the note asks for the room to
 * be arranged so nothing is discovered on arrival. The wording is the client's
 * and deliberately does not spell out the night, the room or the rate — the
 * requirements carry all three (`requiredDate`, `roomIndex`, `benefitValue`),
 * they are simply not rendered, so a future wording can name them without any
 * change upstream.
 *
 * `requirements` is read straight off a booking payload — the API derives it,
 * see modules/booking/core/stayFlexible/stayFlexibleRequirements — or from
 * getStayFlexibleRequirements() while the tunnel is still open, so the note
 * reads the same before and after the booking exists.
 * `translationKey`/`keyPrefix` locate the calling app's wording.
 */
const StayFlexibleRequirements = ({
  requirements,
  currency,
  translationKey = 'booking',
  keyPrefix = 'stayFlexibleRequirements',
  variant = 'warning',
}) => {
  const { t } = useTranslation(translationKey, { keyPrefix });

  if (!requirements?.length) {
    return null;
  }

  /**
   * The wording follows what the booking actually asks for: only the service at
   * hand is named, and both are named only when both are requested.
   */
  const hasEarlyCheckIn = requirements.some(
    ({ service }) => service === StayFlexibleCodeEnum.EARLY_CHECK_IN,
  );
  const hasLateCheckOut = requirements.some(
    ({ service }) => service === StayFlexibleCodeEnum.LATE_CHECK_OUT,
  );
  const serviceKind =
    hasEarlyCheckIn && hasLateCheckOut
      ? 'both'
      : hasEarlyCheckIn
        ? 'earlyCheckIn'
        : 'lateCheckOut';

  return (
    <KoobAlert
      variant={variant}
      icon={variant === 'info' ? 'fa-circle-info' : 'fa-triangle-exclamation'}
      title={t(`title.${serviceKind}`)}
      data-testid="stay-flexible-requirements"
    >
      <p>{t('intro')}</p>
      <p className="mt-2">{t(`supplementIncluded.${serviceKind}`)}</p>
    </KoobAlert>
  );
};

export default StayFlexibleRequirements;
